import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge } from "@workspace/amarmeds-ds/components/ui/badge";
import { Button } from "@workspace/amarmeds-ds/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@workspace/amarmeds-ds/components/ui/card";
import { Input } from "@workspace/amarmeds-ds/components/ui/input";
import { Label } from "@workspace/amarmeds-ds/components/ui/label";

import { api, ApiError, type OpsZone } from "../api";
import { parseTaka, taka } from "../format";

const wavesToText = (waves: OpsZone["waves"]) => waves.map((w) => `${w.cutoff}-${w.deliverBy}`).join(",");

export default function OpsZones() {
  const qc = useQueryClient();
  const zones = useQuery({ queryKey: ["ops-zones"], queryFn: () => api<{ zones: OpsZone[] }>("/ops/zones") });

  const [key, setKey] = useState("");
  const [name, setName] = useState("");
  const [fee, setFee] = useState("");
  const [areas, setAreas] = useState("");
  const [waves, setWaves] = useState("");
  const [active, setActive] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const editing = key !== "";

  const save = useMutation({
    mutationFn: () =>
      api(`/ops/zones`, {
        body: { key, name, deliveryFeePaisa: parseTaka(fee), areas: areas.split(","), waves, active },
      }),
    onSuccess: () => {
      setKey("");
      setName("");
      setFee("");
      setAreas("");
      setWaves("");
      setError(null);
      void qc.invalidateQueries({ queryKey: ["ops-zones"] });
    },
    onError: (err) => setError(err instanceof ApiError ? err.code.replace(/_/g, " ") : "Could not save the zone."),
  });

  const [listMessage, setListMessage] = useState<string | null>(null);
  const remove = useMutation({
    mutationFn: (z: OpsZone) => api(`/ops/zones/${encodeURIComponent(z.key)}`, { method: "DELETE" }),
    onSuccess: (_res, z) => {
      setListMessage(`Deleted zone ${z.name}.`);
      if (key === z.key) setKey("");
      void qc.invalidateQueries({ queryKey: ["ops-zones"] });
    },
    onError: (err, z) =>
      setListMessage(
        err instanceof ApiError && err.code === "zone_in_use"
          ? `${z.name} is used by ${Number(err.body.pharmacies)} pharmacy(s) and ${Number(err.body.orders)} order(s), so it cannot be deleted. Deactivate it instead.`
          : err instanceof ApiError
            ? err.code.replace(/_/g, " ")
            : "Could not delete the zone.",
      ),
  });

  const edit = (z: OpsZone) => {
    setKey(z.key);
    setName(z.name);
    setFee(String(z.deliveryFeePaisa / 100));
    setAreas(z.areas.join(","));
    setWaves(wavesToText(z.waves));
    setActive(z.active);
  };

  const ready = key.trim() !== "" && name.trim() !== "" && parseTaka(fee) !== null && areas.trim() !== "" && waves.trim() !== "";

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold">Zones</h1>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{editing ? `Edit zone ${key}` : "Add a zone"}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="z-key">Key</Label>
              <Input id="z-key" placeholder="mirpur-10" value={key} disabled={editing} onChange={(e) => setKey(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="z-name">Name</Label>
              <Input id="z-name" placeholder="Mirpur 10" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="z-fee">Delivery fee (৳)</Label>
              <Input id="z-fee" inputMode="decimal" placeholder="60" value={fee} onChange={(e) => setFee(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="z-areas">Areas (comma separated)</Label>
              <Input id="z-areas" placeholder="mirpur-10,mirpur-2,kazipara" value={areas} onChange={(e) => setAreas(e.target.value)} />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label htmlFor="z-waves">Waves (HH:MM-HH:MM, comma separated)</Label>
              <Input id="z-waves" placeholder="11:00-15:00,16:00-21:00" value={waves} onChange={(e) => setWaves(e.target.value)} />
            </div>
            <label className="flex items-end gap-2 text-sm">
              <input type="checkbox" className="h-4 w-4" checked={active} onChange={(e) => setActive(e.target.checked)} />
              Active
            </label>
          </div>
          <div className="flex items-center gap-3">
            <Button disabled={!ready || save.isPending} onClick={() => save.mutate()}>
              {editing ? "Save zone" : "Add zone"}
            </Button>
            {editing && (
              <Button variant="outline" onClick={() => { setKey(""); setError(null); }}>
                Cancel
              </Button>
            )}
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Zones</CardTitle>
        </CardHeader>
        <CardContent>
          {zones.isLoading && <p className="text-muted-foreground">Loading…</p>}
          {listMessage && <p className="pb-2 text-sm">{listMessage}</p>}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[680px] text-sm">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th className="p-2">Key</th>
                  <th className="p-2">Name</th>
                  <th className="p-2">Fee</th>
                  <th className="p-2">Areas</th>
                  <th className="p-2">Waves</th>
                  <th className="p-2">Status</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {zones.data?.zones.map((z) => (
                  <tr key={z.key} className="border-t align-top">
                    <td className="p-2 font-medium">{z.key}</td>
                    <td className="p-2">{z.name}</td>
                    <td className="p-2">{taka(z.deliveryFeePaisa)}</td>
                    <td className="p-2">{z.areas.join(", ")}</td>
                    <td className="p-2">{wavesToText(z.waves)}</td>
                    <td className="p-2">
                      <Badge variant={z.active ? "secondary" : "outline"}>{z.active ? "Active" : "Inactive"}</Badge>
                    </td>
                    <td className="p-2">
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="outline" onClick={() => edit(z)}>
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive"
                          disabled={remove.isPending}
                          onClick={() => window.confirm(`Delete zone ${z.name} (${z.key}) for good? This cannot be undone.`) && remove.mutate(z)}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
